import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const CATEGORY_COLOR = {
  gin: "#1A3628",
  whisky: "#8b5a2b",
  rum: "#a67034",
  tequila: "#b8a663",
  mezcal: "#7a5d3a",
  vodka: "#9ec0d8",
  liqueurs: "#b5667d",
  agave: "#a4b35a",
  aperitifs: "#c75a3a",
  wine: "#7a2434",
  beer: "#c79133",
  sake: "#d4d2c4",
  vermouth: "#8b3b3b",
  amaro: "#5e2a2a",
};

export default function SpiritsHome() {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/beverages/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, []);

  const populated = cats.filter((c) => c.id !== "gin" && c.count > 0);
  const empty = cats.filter((c) => c.id !== "gin" && !c.count);
  const total = populated.reduce((a, c) => a + (c.count || 0), 0);

  return (
    <div className="bg-[#0E0F0C] text-white min-h-screen" data-testid="spirits-home">
      {/* Header */}
      <section className="px-5 md:px-12 py-12 md:py-16 max-w-7xl mx-auto">
        <p className="overline text-[#C17F59] mb-3">The Spirits Menu</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4" data-testid="spirits-title">
          Browse BENO's full liquid shelf.
        </h1>
        <p className="text-white/65 max-w-2xl leading-relaxed">
          Whisky, rum, tequila, mezcal, vodka, wine, beer, sake, vermouth, amaro, aperitifs, agave, and the secret cabinet of liqueurs. Gin lives in its own deeper atlas —{" "}
          <Link to="/gin" className="text-[#C17F59] hover:text-[#D89A74] transition-colors underline underline-offset-4">enter the Gin Atlas →</Link>
        </p>
        <p className="font-hand text-2xl text-[#D89A74] mt-5 -rotate-1">
          ~ {total} bottle{total === 1 ? "" : "s"} on the shelf · {populated.length} live categor{populated.length === 1 ? "y" : "ies"}
        </p>
      </section>

      {/* Populated categories */}
      {populated.length > 0 && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto pb-10" data-testid="spirits-live-categories">
          <p className="overline text-white/55 mb-5">Live tonight</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {populated.map((c) => (
              <Link key={c.id} to={`/spirits/${c.id}`}
                    data-testid={`spirits-cat-${c.id}`}
                    className="group relative bg-[#15171A] border border-white/8 hover:border-white/25 p-6 transition-colors overflow-hidden">
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-30 group-hover:opacity-60 transition-opacity"
                     style={{ background: `radial-gradient(circle, ${CATEGORY_COLOR[c.id] || "#888"}, transparent 65%)` }} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-3 h-3 rounded-full" style={{ background: CATEGORY_COLOR[c.id] || "#888" }} />
                    <span className="overline text-white/40">{c.count} bottle{c.count === 1 ? "" : "s"}</span>
                  </div>
                  <h3 className="font-display text-3xl mb-2">{c.label}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{c.description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#C17F59] group-hover:text-[#D89A74] transition-colors">
                    Browse {c.label.toLowerCase()} <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty categories */}
      {empty.length > 0 && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto pb-20" data-testid="spirits-coming-soon">
          <p className="overline text-white/55 mb-5">Coming soon · upload menu to populate</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {empty.map((c) => (
              <Link key={c.id} to={`/spirits/${c.id}`}
                    data-testid={`spirits-empty-${c.id}`}
                    className="group bg-[#15171A]/50 border border-white/5 hover:border-white/20 p-4 transition-colors">
                <div className="w-2 h-2 rounded-full mb-3" style={{ background: CATEGORY_COLOR[c.id] || "#888" }} />
                <p className="font-display text-base">{c.label}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 mt-1">No bottles yet</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Soft nudge back to gin atlas */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto pb-24">
        <Link to="/gin"
              data-testid="spirits-to-gin"
              className="group block bg-[#15171A] border border-[#C17F59]/30 hover:border-[#C17F59] p-7 md:p-10 transition-colors relative overflow-hidden">
          <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
               style={{ background: "radial-gradient(circle, rgba(193,127,89,0.6), transparent 65%)" }} />
          <div className="relative">
            <p className="overline text-[#C17F59] mb-3">The flagship lives next door</p>
            <h3 className="font-display text-2xl md:text-3xl mb-2">Looking for gin?</h3>
            <p className="text-white/65 max-w-xl">
              Every gin gets a 10-axis DNA profile, a matchmaker quiz, a flight builder, and a bartender's note. Gin is BENO's deepest experience — explored in its own atlas.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C17F59] group-hover:text-[#D89A74] transition-colors">
              Enter the Gin Atlas <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      </section>
    </div>
  );
}
